import React, { FC, useMemo } from 'react';

import Ghost from '@gravity-ui/icons/Ghost';
import Person from '@gravity-ui/icons/Person';
import Persons from '@gravity-ui/icons/Persons';
import Shield from '@gravity-ui/icons/Shield';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { SkillAccess } from 'client/model/settings';
import { Channel, InstallableApp, Skill } from 'client/model/skill';
import { getIsAdmin, getIsGlobalViewer } from 'client/utils';
import * as keyset from 'i18n/Header';
import * as settingsKeyset from 'i18n/SettingsLabels';
import * as indexPageKeyset from 'i18n/SkillsIndex';

import { getMapChannelToAppCard } from '../icons/mapChannelToAppCard';

import styles from './DescribeAppInIcons.module.scss';

interface Props {
    app: InstallableApp | Skill;
}

const t = i18n(keyset);
const tIndexPage = i18n(indexPageKeyset);
const tSettings = i18n(settingsKeyset);

const mapChannelToLabel: Record<Channel, string> = {
    [Channel.AutoApp]: tIndexPage('auto'),
    [Channel.TvApp]: tIndexPage('tv'),
    [Channel.SmartScreenApp]: tIndexPage('smart-screen'),
    [Channel.AliceSkill]: '',
    [Channel.SmartHome]: '',
};

const ChannelLabel: FC<{ channel: Channel }> = ({ channel }) => {
    const channelLabel = mapChannelToLabel[channel];
    if (!channelLabel) return null;
    return <span className={styles.blockElement}>{channelLabel}</span>;
};

export const DescribeAppInIcons: FC<Props> = ({ app }) => {
    const mapChannelToAppCard = useMemo(() => getMapChannelToAppCard(), []);

    const displayPrivate = app.onAir
        ? app.skillAccess === SkillAccess.Private
        : app?.draft?.skillAccess === SkillAccess.Private;
    const accessTypeLabel = String(displayPrivate ? tSettings('private') : tSettings('public')).toLowerCase();
    return (
        <div className={styles.describeAppInIconsContainer}>
            {mapChannelToAppCard?.[app?.channel]?.icon}
            <ChannelLabel channel={app?.channel} />
            {' • '}
            {displayPrivate ? <Person /> : <Persons />}
            <span className={styles.blockElement}>{accessTypeLabel}</span>
            {getIsAdmin() && (
                <>
                    {' • '}
                    <div className={clx(styles.roleBlock, styles.roleBlock_admin)}>
                        <Shield />
                        <span className={styles.adminLabel}>{t('admin')}</span>
                    </div>
                </>
            )}
            {getIsGlobalViewer() && (
                <>
                    {' • '}
                    <div className={clx(styles.roleBlock, styles.roleBlock_globalViewer)}>
                        <Ghost />
                        <span className={styles.adminLabel}>{t('viewer')}</span>
                    </div>
                </>
            )}
        </div>
    );
};
